import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Field, Groupper, Header, Icon, Input, Label, Message, Table, Modal, Popup, Menu, Checkbox, Dropdown } from 'react-frontier2';
import { Dropdown as FRDropdown, useFrontier } from 'react-frontier2'
import { SeatStatus, VenueSeat, VenueSection } from '@arema/components/Classes';
import { bindClick, bindFormChange, formatSeatNumber, getNextSeatDenominator, letterToNumber, numberToLetter } from '@arema/components/Util';
import { DEBUG } from '../../../AdminConfig';
import { useTitle } from '@arema/components/Hooks';
import { Seatmap } from '@arema/components';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import SeatmapDrawModal, { SeatGhost } from './SeatmapDrawModal';
import API from '../../../API';
import classNames from 'classnames';
import * as uuid from 'uuid';

import '@arema/components/style/seatmap.scss';
import '../../../style/seatmap_edit.scss';

interface SeatmapSeat extends VenueSeat{
	dirty?: boolean,
	deleted: boolean
}
interface Coordinate{
	x: number,
	y: number,
}

interface SelectionCoordinates{
	group_id?: number,
	ended: boolean,
	start_x: number,
	start_y: number,
	end_x: number,
	end_y: number,
}

interface SelectionCoordinateBounds extends SelectionCoordinates{
	width: number,
	height: number,
}

interface SelectionBounds{
	start_x: number,
	start_y: number,
	end_x: number,
	end_y: number,
	width: number,
	height: number,
}

interface SeatmapClass{
	dirty: boolean,
	seats_width: number,
	seats_height: number,
	stage_x: number,
	stage_y: number,
	stage_width: number,
	stage_height: number,
}

enum ModalType{
	NUMBER = 1,
	SECTION = 2,
	DELETE_SEATS = 3,
	SEAT_COMMENT = 4,
	SEAT_LABEL = 5,
	DRAW_SEATS = 6,
}
enum SeatConfigType {
	NUMBERS = 1,
	LETTERS = 2,
}
enum SeatDirection {
	RIGHT_LEFT = 'rtl',
	LEFT_RIGHT = 'ltr',
	TOP_BOTTOM = 'ttb',
	BOTTOM_TOP = 'btt',
	NO_MOVE = 'nomv',
}

enum SelectMode{
	EVERYTHING = 1,
	SEATS = 2,
	DRAW = 3,
}

interface SeatConfig {
	type?: SeatConfigType,
	direction?: SeatDirection,
	initial_value?: string,
	skip_empty?: boolean,
}

interface SingleConfig{
	row: string,
	number: string
}

const SEAT_WIDTH = 70, SEAT_HEIGHT = 45, SEAT_SPACING = 4;
const PREVIEW_SIZE = 3;
const SEAT_STATUS_LOCALE = [
	{ id: SeatStatus.FREE, 			name: 'Libre', 		class: '',				color: '#e8e8e8', },
	{ id: SeatStatus.BLOCKED,	 	name: 'Bloqueado',	class: 'blocked', 	color: 'brown', },
	{ id: SeatStatus.LOCKED,	 	name: 'Candado', 		class: 'locked',		color: 'gray', },
	{ id: SeatStatus.HIDDEN, 		name: 'Escondido', 	class: 'hidden',		color: 'transparent', },
	{ id: SeatStatus.FREE_PDV,		name: 'Solo PDV',		class: 'free-pdv',	color: '#ff7b7b', },
]

var SeatmapEdit = ()=>{
	var { t } = useTranslation();
	let { venue_id, section_id, date_id: params_date_id } = useParams();
	var { setTitle } = useTitle();
	var { Toast } = useFrontier();
	var [loadError, setLoadError] = useState<string>(null);
	var [section, setSection] = useState<VenueSection>(null);
	var [seats, setSeats] = useState<(SeatmapSeat & { create_id?: string })[]>(null);
	var [ghosts, setGhosts] = useState<SeatGhost[]>([]);
	var [seatmap, setSeatmap] = useState<SeatmapClass>(null);
	var [seatSelection, setSeatSelection] = useState<SelectionCoordinates[]>(null);
	var [configRows, setConfigRows] = useState<SeatConfig>(null);
	var [configCols, setConfigCols] = useState<SeatConfig>(null);
	var [singleConfig, setSingleConfig] = useState<SingleConfig>(null);
	var [shownModal, setShownModal] = useState<ModalType>(null);
	var [sectionName, setSectionName] = useState<string>('');
	var [contextMenu, setContextMenu] = useState<{ x: number, y: number, left: boolean, bottom: boolean }>(null);
	var [sending, setSending] = useState<boolean>(false);
	var [deleteConfirm, setDeleteConfirm] = useState<string>('');
	var [seatComment, setSeatComment] = useState<string>('');
	var [seatStatus, setSeatStatusM] = useState<number>(null);
	var [seatSize, setSeatSize] = useState<number>(0.8);
	var [selectMode, setSelectMode] = useState<SelectMode>(SelectMode.EVERYTHING);
	var [createStack, setCreateStack] = useState<string[]>([]);
	var [drawSettings, setDrawSettings] = useState<{
		seat_row: string,
		seat_number: string,
		seat_section: string,
		move_row: SeatDirection,
		move_number: SeatDirection,
		last_draw: Coordinate,
	}>(null);
	var [hoverSeat, setHoverSeat] = useState<Coordinate>(null);
	var firstSeatRef = useRef(null);
	var date_id : number = typeof params_date_id !== 'undefined' ? parseInt(params_date_id.toString()) : null;

	var deselect = ()=>{
		setSeatSelection([]);
		setContextMenu(null);
	}

	useHotkeys('meta+z,ctrl+z', ()=>{
		if(selectMode===SelectMode.DRAW){
			if(!createStack) return;
			var cs = [...createStack];
			var cid = cs.pop();
			if(!cid) return;
			var sts = getSeatsClone();
			setSeats(sts.filter(a=>a.create_id!==cid || a.seat_id!==null));
			setCreateStack(cs);
			setDrawSettings(d=>({
				...d,
				last_draw: null
			}))
		}else{
			popSelection();
		}
	}, {
		preventDefault: true,
	}, [seatSelection, selectMode, createStack, seats]);
	useHotkeys('meta+d,ctrl+d', ()=>{
		deselect()
	}, {
		preventDefault: true
	}, [seatSelection]);
	useHotkeys('w,s,a,d', (d)=>{
		if(selectMode!==SelectMode.DRAW || !drawSettings?.seat_row) return;
		var new_row = (d.key==='w' || d.key==='s') ? getNextSeatDenominator(drawSettings.seat_row, d.key=='w' ? 1 : -1) : drawSettings.seat_row;
		var new_number = (d.key==='a' || d.key==='d') ? getNextSeatDenominator(drawSettings.seat_number, d.key=='d' ? 1 : -1) : drawSettings.seat_number;
		setDrawSettings(d=>({
			...d,
			seat_row: new_row,
			seat_number: new_number
		}))
	}, [drawSettings, selectMode])

	useEffect(()=>{
		if(!seats && !Number.isNaN(parseInt(venue_id)) && !Number.isNaN(parseInt(section_id))){
			API.getVenueSection(parseInt(venue_id), parseInt(section_id), date_id, true).then(res=>{
				if(res.error) return setLoadError(res.message);
				var seats = [...res.data.seats];
				delete res.data.seats;
				setSection(res.data);
				setTitle(`Numeración: ${res.data.section_name}`);
				setSeatmap({
					dirty: false,
					seats_width: res.data.seats_width,
					seats_height: res.data.seats_height,
					stage_x: res.data.stage_x,
					stage_y: res.data.stage_y,
					stage_width: res.data.stage_width,
					stage_height: res.data.stage_height,
				});
				setSeats(seats.map(a=>({
					...a,
					deleted: false,
				})));
			}).catch(err=>{
				if(DEBUG) console.log(err);
				setLoadError('Hubo un error inesperado cargando el mapa de butacas. (LCL-1)');
			});
		}
	}, []);

	var isSelected = (x: number, y: number)=>{
		if(!seatSelection || seatSelection.length==0) return false;
		for(var i of seatSelection){
			var start_x = Math.min(i.start_x, i.end_x), end_x = Math.max(i.start_x, i.end_x);
			var start_y = Math.min(i.start_y, i.end_y), end_y = Math.max(i.start_y, i.end_y);
			if(x>=start_x && y>=start_y && x<=end_x && y<=end_y){
				if(selectMode===SelectMode.SEATS && i.ended){
					var is_seat = !!seats.find(a=>a.seat_x===x && a.seat_y===y && !a.deleted);
					if(is_seat) return true;
					var is_ghost = !!ghosts.find(a=>a.x===x && a.y===y);
					return is_ghost;
				}
				return true;
			}
		}
		return false;
	}

	var getSelectedSeats = ()=>{
		if(!seatSelection || seatSelection.length==0) return []
		return seats.filter(a=>isSelected(a.seat_x, a.seat_y) && !a.deleted);
	}

	var getSelectionCoords = () : SelectionCoordinateBounds[]=>{
		if(!seatSelection) return null;
		var coords : SelectionCoordinateBounds[] = [];
		for(var i of seatSelection){
			var start_x = Math.min(i.start_x, i.end_x), end_x = Math.max(i.start_x, i.end_x);
			var start_y = Math.min(i.start_y, i.end_y), end_y = Math.max(i.start_y, i.end_y);
			coords.push({
				ended: i.ended,
				start_x, start_y,
				end_x, end_y,
				width: end_x-start_x+1,
				height: end_y-start_y+1,
			});
		}
		return coords;
	}

	var coordsToBounds = (coords: SelectionCoordinates[])=>{
		var bounds : SelectionBounds = {
			start_x: 1000000,	start_y: 1000000,
			end_x: -1, 		end_y: -1,
			width: null, 	height: null
		}
		for(var i of coords){
			bounds.start_x = Math.min(bounds.start_x, i.start_x);
			bounds.start_y = Math.min(bounds.start_y, i.start_y);
			bounds.end_x = Math.max(bounds.end_x, i.end_x);
			bounds.end_y = Math.max(bounds.end_y, i.end_y);
		}
		bounds.width = bounds.end_x-bounds.start_x+1;
		bounds.height = bounds.end_y-bounds.start_y+1;
		return bounds;
	}

	var getSelectionBounds = () : SelectionBounds=>{
		if(!seatSelection || seatSelection.length==0) return null;
		return coordsToBounds(getSelectionCoords());
	}

	var getSeatSize = (size: number)=>{
		var seat_width = SEAT_WIDTH*size;
		var seat_height = SEAT_HEIGHT*size;
		var seat_spacing = SEAT_SPACING*size;
		var text_style: React.CSSProperties = {
			fontSize: Math.min(14*size, 14),
			lineHeight: `${Math.min(14*size, 14)+2}px`,
		}
		var seat_style : React.CSSProperties = {
			width: seat_width,
			height: seat_height,
			maxWidth: seat_width,
			maxHeight: seat_height,
			minWidth: seat_width,
			minHeight: seat_height,
			margin: seat_spacing/2,
			borderRadius: Math.max(8*size, 6),
		}

		return { seat_width, seat_height, seat_spacing, seat_style, text_style };
	}

	var has_selection = useMemo(()=>{
		return !!seatSelection && seatSelection.length>0;
	}, [seatSelection]);
	var real_selection = useMemo(()=>{
		return getSelectionCoords();
	}, [seatSelection]);
	var selection_bounds = useMemo(()=>{
		return getSelectionBounds();
	}, [seatSelection]);
	var seats_selected = useMemo(()=>{
		return getSelectedSeats();
	}, [seatSelection, seats]);
	var stage_dirty = useMemo(()=>{
		return (seats && seats.filter(a=>a.dirty || !a.seat_id).length>0) || seatmap?.dirty
	}, [seats, seatmap]);
	var stage_selected = useMemo(()=>{
		if(!real_selection || real_selection.length==0 || !seatmap) return false;
		var has_stage = seatmap && seatmap.stage_height>0 && seatmap.stage_width>0 && seatmap.stage_x!==null && seatmap.stage_y!==null;
		if(!has_stage) return false;
		for(var i of real_selection){
			if(seatmap.stage_x===i.start_x && seatmap.stage_y===i.start_y && seatmap.stage_width===i.width && seatmap.stage_height===i.height){
				return true;
			}
		}
		return false;
	}, [real_selection, seatmap]);
	var effective_seat_size = useMemo(()=>{
		return getSeatSize(seatSize || 1);
	}, [seatSize]);

	var { map, seat_duplicates } = useMemo(()=>{
		var seat_denominators : string[] = [];
		var seat_duplicates : string[] = [];
		var map : SeatmapSeat[][] = [];

		if(!seatmap || !seats) return { map, seat_duplicates };

		for(let y=0; y<seatmap.seats_height; y++){
			var row : SeatmapSeat[] = [];
			for(let x=0; x<seatmap.seats_width; x++){
				var seat = seats.find(a=>!a.deleted && a.seat_x===x && a.seat_y===y);
				if(seat && (seat.seat_row!==null || seat.seat_number!==null)){
					seat_denominators.push(formatSeatNumber(seat.seat_row, seat.seat_number)+(seat.seat_section && seat.seat_section.length>0 ? `-${seat.seat_section.toUpperCase()}` : ''));
				}
				row.push(seat || null);
			}
			map.push(row);
		}
		seat_denominators = seat_denominators.sort();
	
		for(var sdi=0; sdi<seat_denominators.length-1; sdi++){
			if(seat_denominators[sdi+1]===seat_denominators[sdi]){
				seat_duplicates.push(seat_denominators[sdi]);
			}
		}

		return {
			map,
			seat_duplicates,
		}
	}, [seatmap, seats]);

	if(Number.isNaN(parseInt(venue_id)) || Number.isNaN(parseInt(section_id))){
		return <Header text='404' subtext='No se encontró el mapa del recinto.' />
	}
	if(loadError){
		return <Header text='Error' subtext={loadError} />
	}
	if(seats===null){
		return <Header loading text='Cargando butacas...' />
	}

	var getSeatsClone = ()=>{
		return seats.map(a=>({ ...a }));
	}

	var getSeatCoordsFromCoords = (x: number, y: number)=>{
		var { x: smX, y: smY } = firstSeatRef.current?.getBoundingClientRect();
		var { seat_width, seat_height, seat_spacing } = effective_seat_size;

		var seat_x = Math.floor((x-smX)/(seat_width+seat_spacing));
		var seat_y = Math.floor((y-smY)/(seat_height+seat_spacing));
		if(seat_x<0 || seat_y<0) return null;
		if(seat_x>=seatmap.seats_width || seat_y>=seatmap.seats_height) return null;
		seat_x = seat_x >= seatmap.seats_width ? (seatmap.seats_width-1) : seat_x;
		seat_y = seat_y >= seatmap.seats_height ? (seatmap.seats_height-1) : seat_y;
		return {
			x: seat_x,
			y: seat_y,
		}
	}

	var getActiveSelection = ()=>{
		if(!seatSelection || seatSelection.length==0) return { index: -1, selection: null };
		var ix = seatSelection.findIndex(a=>!a.ended);
		return {
			index: ix,
			selection: ix==-1 ? null : seatSelection[ix]
		}
	}

	var moveSelection = (ix: number, end_x: number, end_y: number)=>{
		var sel = [...seatSelection];
		sel[ix].end_x = end_x;
		sel[ix].end_y = end_y;
		setSeatSelection(sel);
	}

	var endSelection = ()=>{
		var sel = [...(seatSelection || [])];
		var ix = sel ? sel.findIndex(a=>!a.ended) : -1;
		if(ix==-1) return;
		sel[ix].ended = true;
		setSeatSelection(sel);
	}

	var startSelection = (seat_x: number, seat_y: number, empty: boolean=true, ended: boolean=false)=>{
		if(selectMode===SelectMode.DRAW) return;
		var sel = [];
		if(!empty && seatSelection){
			sel.push(...seatSelection);
		}
		sel.push({
			ended,
			start_x: seat_x, start_y: seat_y,
			end_x: seat_x, end_y: seat_y,
		});
		setSeatSelection(sel);
	}

	var onSelectStart = (e: React.MouseEvent<HTMLDivElement, MouseEvent>)=>{
		setContextMenu(null);
		var seat = getSeatCoordsFromCoords(e.clientX, e.clientY);
		if(selectMode===SelectMode.DRAW){
			return stampDrawMode(seat);
		}
		if(e.button==2 && seat){
			if(!isSelected(seat.x, seat.y)){
				// startSelection(seat.x, seat.y, !(e.metaKey || e.shiftKey), true);
				startSelection(seat.x, seat.y, false, true);
			}
			return;
		}else if(e.button!==0) return;
		if(!seat) return setSeatSelection([]);
		// startSelection(seat.x, seat.y, !(e.metaKey || e.shiftKey));
		startSelection(seat.x, seat.y, false);
	}

	var onSelectMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>)=>{
		var { index, selection } = getActiveSelection();
		if((!selection || index==-1) && selectMode!==SelectMode.DRAW) return;
		var seat = getSeatCoordsFromCoords(e.clientX, e.clientY);
		if(selectMode===SelectMode.DRAW){
			if(!!hoverSeat!==!!seat || !seat) return setHoverSeat(seat);
			if(hoverSeat.x!==seat.x || hoverSeat.y!==seat.y) return setHoverSeat(seat);
			return;
		}

		if(seat && (selection.end_x!=seat.x || selection.end_y!=seat.y)){
			moveSelection(index, seat.x, seat.y);
		}
	}

	var popSelection = ()=>{
		setSeatSelection(seatSelection.length>0 ? seatSelection.slice(0, seatSelection.length-1) : []);
	}

	var onSelectEnd = (e: React.MouseEvent<HTMLDivElement, MouseEvent>)=>{
		endSelection()
		// if(!selection || selection.ended) return;
		// if(selection.end_x<0 || selection.end_y<0) return setSelection(null);
		// setSelection({
		// 	...selection,
		// 	ended: true
		// })
	}

	const CONTEXT_MENU_HEIGHT = 5*40; // Number of items * 40
	var onContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>)=>{
		var seat = getSeatCoordsFromCoords(e.clientX, e.clientY);
		if(!seat) return;
		e.preventDefault();
		var left = seat.x<(seatmap.seats_width/2);
		var bottom = seat.y<(seatmap.seats_height/2);
		setContextMenu({ x: e.clientX-(!left ? 200 : 0), y: e.clientY-(!bottom ? CONTEXT_MENU_HEIGHT : 0), left, bottom });
	}

	var isStageSelected = ()=>{
		for(let c of getSelectionCoords()){
			for(var x=c.start_x; x<c.end_x+1; x++){
				for(var y=c.start_y; y<c.end_y+1; y++){
					if(isStage(x, y)) return true;
				}
			}
		}
		return false;
	}

	var isStage = (x: number, y: number)=>{
		return !!seatmap.stage_width && (seatmap.stage_x<=x && (seatmap.stage_x+seatmap.stage_width)>x) && (seatmap.stage_y<=y && (seatmap.stage_y+seatmap.stage_height)>y);
	}

	var getSelectedSeatsIndex = ()=>{
		var indexes : number[] = [];
		for(var i=0; i<seats.length; i++){
			if(!seats[i].deleted && isSelected(seats[i].seat_x, seats[i].seat_y)){
				indexes.push(i);
			}
		}
		return indexes;
	}

	var fixStartEnd = (start: number, end: number)=>{
		return {
			start: Math.min(start, end),
			end: Math.max(start, end),
		}
	}

	var selectModeChanged = (mode: SelectMode)=>{
		return ()=>{
			setSelectMode(mode);
		}
	}

	var updateMapColumns = (amount: number)=>{
		if(sending) return;
		var max_x = Math.max(...seats.map(a=>a.seat_x), seatmap.stage_x+seatmap.stage_width-1);
		if((seatmap.seats_width+amount)<=max_x){
			return Toast.error('No se puede hacer mas pequeño el mapa de butacas.')
		}

		setSeatmap({
			...seatmap,
			dirty: true,
			seats_width: seatmap.seats_width+amount,
		})
	}

	var updateMapRows = (amount: number)=>{
		if(sending) return;
		var max_y = Math.max(...seats.map(a=>a.seat_y), seatmap.stage_y+seatmap.stage_height-1);
		if((seatmap.seats_height+amount)<=max_y){
			return Toast.error('No se puede hacer mas pequeño el mapa de butacas.')
		}

		setSeatmap({
			...seatmap,
			dirty: true,
			seats_height: seatmap.seats_height+amount,
		})
	}

	var checkValidType = (val: string, type: SeatConfigType) => {
		if (!val || val.length === 0) return true;
		if (type === SeatConfigType.NUMBERS) {
			return /[0-9]+/.test(val);
		} else if (type === SeatConfigType.LETTERS) {
			return /[a-z]+/i.test(val);
		} else return true;
	}

	var isConfigValid = (config: SeatConfig)=>{
		return config && config.direction && config.initial_value && config.type && checkValidType(config.initial_value, config.type);
	}

	var getPreview = (coords: SelectionCoordinates[]): { row: string, number: string }[][] => {
		if(!coords || coords.length==0) return [];
		var bounds = coordsToBounds(coords);
		var preview_seats: { row: string, number: string }[][] = [];
		var empty_cols : number[] = [];
		for(var i=0; i<bounds.height; i++){
			var r = [];
			for(var j=0; j<bounds.width; j++){
				r.push(isSelected(bounds.start_x+j, bounds.start_y+i) ? { row: null, number: null } : null);
			}
			preview_seats.push(r);
		}

		for(var x=0; x<bounds.width; x++){
			var has_seat : boolean = false;
			for(var y=0; y<bounds.height; y++){
				if(!!preview_seats[y][x]){
					has_seat = true;
					break;
				}
			}
			if(!has_seat) empty_cols.push(x);
		}
		
		var getInitialVal = (type: SeatConfigType, val: string)=>type===SeatConfigType.LETTERS ? letterToNumber(val) : parseInt(val);

		if(isConfigValid(configRows)){
			var initval = getInitialVal(configRows.type, configRows.initial_value);
			var real_y = 0;
			var start_y = configRows.direction===SeatDirection.BOTTOM_TOP ? preview_seats.length-1 : 0;
			for(var y=start_y; configRows.direction===SeatDirection.BOTTOM_TOP ? y>=0 : y<preview_seats.length;){
				var real_seats = 0;
				for(var x=0; x<preview_seats[y].length; x++){
					if(preview_seats[y][x]){
						var v = (initval + real_y);
						preview_seats[y][x].row = configRows.type === SeatConfigType.NUMBERS ? v.toString() : numberToLetter(v);
						real_seats++;
					}
				}
				if(configRows.skip_empty) real_y++
				else if(real_seats>0) real_y++;

				if(configRows.direction===SeatDirection.BOTTOM_TOP) y--;
				else y++;
			}
		}

		if(isConfigValid(configCols)){
			var initval = getInitialVal(configCols.type, configCols.initial_value);

			for(var y=0; y<preview_seats.length; y++){
				var real_x = 0;
				var start_x = configCols.direction===SeatDirection.RIGHT_LEFT ? preview_seats[y].length-1 : 0;
				for(var x=start_x; configCols.direction===SeatDirection.RIGHT_LEFT ? x>=0 : x<preview_seats[y].length;){
					if(preview_seats[y][x]){
						var v = initval + real_x;
						preview_seats[y][x].number = configCols.type === SeatConfigType.NUMBERS ? v.toString() : numberToLetter(v);
					}

					if(configCols.skip_empty) real_x++;
					else if(empty_cols.indexOf(x)===-1) real_x++;

					if(configCols.direction===SeatDirection.RIGHT_LEFT) x--;
					else x++;
				}
			}
		}

		return preview_seats;
	}

	var startDrawMode = ()=>{
		deselect();
		setSelectMode(SelectMode.DRAW);
		setDrawSettings(d=>({
			seat_row: 'A',
			seat_number: '1',
			seat_section: null,
			move_row: SeatDirection.TOP_BOTTOM,
			move_number: SeatDirection.LEFT_RIGHT,
			...d,
			last_draw: null,
		}))
	}

	var getDrawSeat = (next_seat: Coordinate, original_seat: Coordinate)=>{
		var seat_row = drawSettings.seat_row, seat_number = drawSettings.seat_number;
		if(!!original_seat){
			var x_diff = next_seat.x-original_seat.x, x_move = x_diff==0 ? 0 : x_diff>0 ? 1 : -1;
			var y_diff = next_seat.y-original_seat.y, y_move = y_diff==0 ? 0 : y_diff>0 ? 1 : -1;
			if(y_move!=0 && drawSettings.move_row!==SeatDirection.NO_MOVE && seat_row && seat_row.length>0){
				seat_row = getNextSeatDenominator(seat_row, (drawSettings.move_row===SeatDirection.BOTTOM_TOP ? -1 : 1) * y_move);
			}
			if(x_move!=0 && drawSettings.move_number!==SeatDirection.NO_MOVE && seat_number && seat_number.length>0){
				seat_number = getNextSeatDenominator(seat_number, (drawSettings.move_number===SeatDirection.RIGHT_LEFT ? -1 : 1) * x_move);
			}
		}

		seat_row = seat_row ? seat_row.toUpperCase() : null;
		seat_number = seat_number ? seat_number.toUpperCase() : null;

		return { seat_row, seat_number };
	}

	var stampDrawMode = (seat: Coordinate)=>{
		if(!seat || isStage(seat.x, seat.y)) return;
		var new_seats = getSeatsClone();
		var sx = new_seats.findIndex(a=>a.seat_x===seat.x && a.seat_y===seat.y);
		var seat_row = drawSettings.seat_row, seat_number = drawSettings.seat_number;
		if(!!drawSettings.last_draw){
			var ds = getDrawSeat(seat, drawSettings.last_draw);
			seat_row = ds.seat_row;
			seat_number = ds.seat_number;
		}

		// console.log({ seat_row, seat_number	});

		var create_id = uuid.v4();
		if(sx===-1){
			new_seats.push({
				create_id,
				seat_id: null,
				deleted: false,
				seat_number: seat_number?.toUpperCase() || null,
				seat_row: seat_row?.toUpperCase() || null,
				seat_section: drawSettings?.seat_section || null,
				seat_status: SeatStatus.FREE,
				seat_x: seat.x,
				seat_y: seat.y,
				section_id: null,
				dirty: true,
			})
			setCreateStack(cs=>[...cs, create_id]);
		}else{
			// new_seats[sx].seat_row = drawSettings.
		}

		setSeats(new_seats);
		setDrawSettings(d=>({
			...d,
			seat_row,
			seat_number,
			last_draw: seat,
		}));
	}

	var showNumberModal = ()=>{
		if(!seatSelection || seatSelection.length==0 || sending) return;
		setContextMenu(null);
		if(isStageSelected()) return Toast.error('No se puede numerar el escenario.');
		
		setConfigRows({
			direction: SeatDirection.TOP_BOTTOM,
			skip_empty: true,
		});
		setConfigCols({
			direction: SeatDirection.LEFT_RIGHT,
			skip_empty: true,
		});
		var coords = getSelectionCoords();
		if(coords.length==1 && (coords[0].width*coords[0].height)==1){
			var seat = seats_selected && seats_selected.length>0 ? seats_selected[0] : null;
			setSingleConfig({
				row: seat?.seat_row || '',
				number: seat?.seat_number || '',
			});
		}
		setShownModal(ModalType.NUMBER);
	}

	var determineConfigType = (config: SeatConfig, setConfig: (c: SeatConfig)=>void)=>{
		return (val: string)=>{
			setConfig({
				...config,
				type: checkValidType(val, SeatConfigType.LETTERS) ? SeatConfigType.LETTERS : (checkValidType(val, SeatConfigType.NUMBERS) ? SeatConfigType.NUMBERS : null),
				initial_value: val
			});
		}
	}

	var numberSeats = ()=>{
		if(!seatSelection || seatSelection.length==0 || sending) return;
		if(!isConfigValid(configCols) && !isConfigValid(configRows)) return;

		
		// Check if stage overlaps
		if(isStageSelected()) return Toast.error('No se puede numerar el escenario.');

		var coords = getSelectionCoords();
		var bounds = coordsToBounds(coords);
		var numbered_seats = getPreview(coords);
		var new_seats = getSeatsClone();

		var y = bounds.start_y;
		for(var row of numbered_seats){
			var x = bounds.start_x;
			for(var s of row){
				if(s){
					var six = new_seats.findIndex(a=>a.seat_x==x && a.seat_y==y && !a.deleted);
					if(six===-1){
						new_seats.push({
							seat_id: null,
							section_id: section.section_id,
							seat_x: x,
							seat_y: y,
							seat_row: s.row,
							seat_number: s.number,
							seat_section: null,
							seat_status: SeatStatus.FREE,
							deleted: false,
						});
					}else{
						new_seats[six].seat_row = s.row;
						new_seats[six].seat_number = s.number;
						new_seats[six].dirty = true;
					}
				}
				x++;
			}
			y++;
		}
		setSeats(new_seats);
		setShownModal(null);
	}

	var numberSingleSeat = ()=>{
		if(!singleConfig || !(singleConfig.number.length>0 || singleConfig.row.length>0)){
			return Toast.error("La configuración de la numeración no es válida.");
		}
		var new_seats = getSeatsClone();
		var selection = getSelectionCoords();
		var edit_seats = getSelectedSeatsIndex();
		if(edit_seats.length==1){
			new_seats[edit_seats[0]].seat_row = singleConfig.row?.toUpperCase();
			new_seats[edit_seats[0]].seat_number = singleConfig.number?.toUpperCase();
			new_seats[edit_seats[0]].dirty = true;
		}else if(selection && selection.length==1 && (selection[0].width*selection[0].height)==1){
			new_seats.push({
				seat_id: null,
				section_id: section.section_id,
				seat_x: selection[0].start_x,
				seat_y: selection[0].start_y,
				seat_row: singleConfig.row.toUpperCase(),
				seat_number: singleConfig.number.toUpperCase(),
				seat_section: null,
				seat_status: SeatStatus.FREE,
				deleted: false,
			});
		}else{
			return Toast.error('Se esta usando la herramienta de edición simple en multiples butacas.');
		}
		
		setSeats(new_seats);
		setShownModal(null);
		Toast.success('Se ha numerado la butaca seleccionada.');
	}

	var setSeatStatus = (status_id: number = null)=>{
		if(sending) return false;
		setContextMenu(null);
		var selected = getSelectedSeatsIndex();
		if(selected.length==0) return;
		var new_seats = getSeatsClone();
		for(var i of selected){
			new_seats[i].seat_status = status_id ? status_id : seatStatus;
			new_seats[i].status_comment = seatComment;
			new_seats[i].dirty = true;
		}
		setSeatComment(null);
		setSeats(new_seats);
		setShownModal(null);
		Toast.success('Se ha asignado el estado de las butacas');
	}

	var setSeatSection = ()=>{
		if(sending) return false;
		var selected = getSelectedSeatsIndex();
		var new_seats = getSeatsClone();
		if(selected.length>0){
			for(var i of selected){
				new_seats[i].seat_section = sectionName.length==0 ? null : sectionName;
				new_seats[i].dirty = true;
			}
		}else{
			var selcoords = getSelectionCoords();
			// console.log(selcoords);
			for(var s of selcoords){
				for(var x=s.start_x; x<=s.end_x; x++){
					for(var y=s.start_y; y<=s.end_y; y++){
						new_seats.push({
							seat_id: null,
							section_id: section.section_id,
							seat_x: x,
							seat_y: y,
							seat_row: null,
							seat_number: null,
							seat_section: sectionName,
							seat_status: SeatStatus.FREE,
							deleted: false,
							dirty: true
						})
					}
				}
			}
			// return Toast.error('No se han seleccionado butacas')
			// new_seats.push()
		}
		setSeats(new_seats);
		setShownModal(null);
		Toast.success('Se ha asignado la sección de las butacas');
	}

	var moveSeats = (x: number, y: number)=>{
		return ()=>{
			if(sending) return false;
			var selected = getSelectedSeatsIndex();
			if(selected.length==0) return;
			var new_seats = getSeatsClone().filter((a, ix)=>selected.indexOf(ix)==-1);
			var moved_seats = getSeatsClone().filter((a, ix)=>selected.indexOf(ix)>-1);

			for(var i of moved_seats){
				i.seat_x += x;
				i.seat_y += y;
				i.dirty = true;
				var sx = new_seats.findIndex(a=>a.seat_x==i.seat_x && a.seat_y==i.seat_y && !a.deleted);

				// Check if seat pos is negative
				if(i.seat_x<0 || i.seat_y<0 || i.seat_y>=seatmap.seats_height || i.seat_x>=seatmap.seats_width){
					return Toast.error('Las butacas no se pueden salir del mapa de butacas.');
				}
				// Check if other overlaps other seats
				if(sx!=-1){
					return Toast.error('No se puede mover las butacas en esa dirección ya que ya existe una butaca en esa posición.');
				}
				// Check if touches stage
				if(isStage(i.seat_x, i.seat_y)){
					return Toast.error('Las butacas topan con el escenario.')
				}
			}

			if(seatSelection && seatSelection.length>0){
				var sel = [...seatSelection];
				for(var s of sel){
					s.start_x += x;
					s.start_y += y;
					s.end_x += x;
					s.end_y += y;
				}
				setSeatSelection(sel);
			}
			setSeats([
				...new_seats,
				...moved_seats,
			])
		}
	}

	var setStage = ()=>{
		if(sending) return false;
		setContextMenu(null);
		if(!seatSelection || seatSelection.length==0) return Toast.error('No se ha seleccionado nada en el mapa para el escenario');
		if(getSelectedSeats().length>0) return Toast.error('No se puede asignar escenario a butacas.');
		var bounds = getSelectionBounds();
		setSeatmap({
			...seatmap,
			dirty: true,
			stage_x: bounds.start_x,
			stage_y: bounds.start_y,
			stage_width: bounds.width,
			stage_height: bounds.height,
		});
	}

	var removeStage = ()=>{
		if(sending) return false;
		setContextMenu(null);
		setSeatmap({
			...seatmap,
			dirty: true,
			stage_x: null,
			stage_y: null,
			stage_width: null,
			stage_height: null
		});
	}

	var getDeleteConfirmation = ()=>{
		return getSelectedSeats().map(a=>formatSeatNumber(a.seat_row, a.seat_number)).slice(0, 5).join(', ');
	}

	var addRow = (row_y: number)=>{
		if(sending) return false;
		setContextMenu(null);
		if(row_y===null) return;
		var new_seats = getSeatsClone();
		for(var i of new_seats){
			if(i.seat_y>row_y && !i.deleted){
				i.seat_y += 1;
				i.dirty = true;
			}
		}
		setSeatmap({
			...seatmap,
			dirty: true,
			seats_height: seatmap.seats_height+1,
			stage_y: seatmap.stage_y+(seatmap.stage_y>row_y ? 1 : 0),
		});
		if(seatSelection && seatSelection.length>0){
			var sel = [...seatSelection];
			for(var s of sel){
				var { start: start_y, end: end_y } = fixStartEnd(s.start_y, s.end_y);
				if(start_y>=row_y) start_y += 1;
				if(end_y>= row_y) end_y += 1;
				s.start_y = start_y;
				s.end_y = end_y;
			}
		}
		setSeats(new_seats);
	}

	var addColumn = (col_x: number)=>{
		if(sending) return false;
		setContextMenu(null);
		if(col_x===null) return;
		var new_seats = getSeatsClone();
		for(var i of new_seats){
			if(i.seat_x>col_x && !i.deleted){
				i.seat_x += 1;
				i.dirty = true;
			}
		}
		setSeatmap({
			...seatmap,
			dirty: true,
			seats_width: seatmap.seats_width+1,
			stage_x: seatmap.stage_x+(seatmap.stage_x>col_x ? 1 : 0),
		});
		if(seatSelection && seatSelection.length>0){
			var sel = [...seatSelection];
			for(var s of sel){
				var { start: start_x, end: end_x } = fixStartEnd(s.start_x, s.end_x);
				if(start_x>=col_x) start_x += 1;
				if(end_x>= col_x) end_x += 1;
				s.start_x = start_x;
				s.end_x = end_x;
			}
		}
		setSeats(new_seats);
	}

	var removeRow = (start_y: number, end_y: number)=>{
		setContextMenu(null);
		if(start_y===null || end_y ===null) return;
		var new_seats = getSeatsClone();
		let length = 0;
		for(var i = start_y; i <=end_y; i++){
			length++
			let row_y = i;
			var row_seats = new_seats.filter(a=>a.seat_y==row_y && !a.deleted);
			if(row_seats.length>0){
				return Toast.error('No se puede eliminar la fila ya que hay butacas en esta fila.');
			}
			if(seatmap.stage_width && seatmap.stage_y<=row_y && (seatmap.stage_y+seatmap.stage_height-1)>=row_y){
				return Toast.error('No se puede eliminar la fila ya que el escenario se encuentra en esta fila.')
			}
		}
		
		for(var j of new_seats){
			if(j.seat_y>end_y && !j.deleted){
				j.seat_y = j.seat_y - length;
				j.dirty = true;
			}
		}
		setSeatmap({
			...seatmap,
			dirty: true,
			seats_height: seatmap.seats_height-length,
			stage_y: seatmap.stage_y-(seatmap.stage_y>start_y ? length : 0),
		});
		setSeats(new_seats);
	}

	var removeColumn = (col_x: number)=>{
		setContextMenu(null);
		if(col_x===null) return;
		var new_seats = getSeatsClone();
		
		var col_seats = new_seats.filter(a=>a.seat_x==col_x && !a.deleted);
		if(col_seats.length>0){
			return Toast.error('No se puede eliminar la columna ya que hay butacas en esta columna.');
		}

		if(seatmap.stage_width && seatmap.stage_x<=col_x && (seatmap.stage_x+seatmap.stage_width-1)>=col_x){
			return Toast.error('No se puede eliminar la columna ya que el escenario se encuentra en esta columna.')
		}

		for(var i of new_seats){
			if(i.seat_x>col_x && !i.deleted){
				i.seat_x -= 1;
				i.dirty = true;
			}
		}

		setSeatmap({
			...seatmap,
			dirty: true,
			seats_width: seatmap.seats_width-1,
			stage_x: seatmap.stage_x-(seatmap.stage_x>col_x ? 1 : 0),
		});
		// setSelection({
		// 	...selection,
		// 	start_x: selection.start_x-1,
		// 	end_x: selection.end_x-1,
		// })	
		setSeats(new_seats);
	}

	var showDeleteModal = async () => {
		var delete_seats = getSelectedSeats()
		if(delete_seats.length==0) return;
		var all_local = true;
		for(var i of delete_seats){
			if(!!i.seat_id){
				all_local = false;
			}
		}
		if(all_local){
			setDeleteConfirm(DEBUG ? getDeleteConfirmation() : '');
			setShownModal(ModalType.DELETE_SEATS);
			return;
		}

		try{
			var res = await API.isSeatDeletable(delete_seats.map(s => s.seat_id));
			if(res.error) return Toast.error(res.message);

			var not_deletable = res.data.filter(s=>!s.deletable);
			if(not_deletable.length>0) {
				var seatsid = not_deletable.map(s=>s.seat_id);
				let butacasError = `Las siguientes butacas no se pueden eliminar: ${seatsid.join()}`;
				Toast.error(butacasError);
			}else{
				setContextMenu(null);
				setDeleteConfirm(DEBUG ? getDeleteConfirmation() : '');
				setShownModal(ModalType.DELETE_SEATS);
			}
		}catch(e){
			return Toast.error(t('common.unexpected_code', { code: 'LCL-STEDDLMDL-1' }));
		}
	}

	var deleteSeats = ()=>{
		var delete_seats = getSelectedSeatsIndex();
		if(delete_seats.length==0) return;
		var new_seats = getSeatsClone();
		for(var i of delete_seats){
			new_seats[i].deleted = true;
			new_seats[i].dirty = true;
		}
		setSeats(new_seats);
		Toast.success('Se han eliminado las butacas, no se eliminarán por completo hasta guardar el mapa.');
		setShownModal(null);
	}

	var sendSave = ()=>{
		if(sending) return;
		var seats = getSeatsClone();
		// Solo las butacas que ya existian o que son nuevas y no estan eliminadas.
		var updated_seats = seats.filter(a=>a.seat_id || (!a.seat_id && !a.deleted)).map(a=>({
			seat_id: a.seat_id || null,
			seat_row: a.seat_row,
			seat_number: a.seat_number,
			seat_section: a.seat_section,
			seat_x: a.seat_x,
			seat_y: a.seat_y,
			seat_status: a.seat_status,
			status_comment: a.status_comment,
			deleted: a.deleted,
		}));
		if(seat_duplicates && seat_duplicates.length>0){
			return Toast.error(`Hay butacas con denominadores duplicados, favor de arreglarlos. (${seat_duplicates.join(', ')})`);
		}
		setSending(true);
		API.saveSeats(section.section_id, date_id, seatmap, updated_seats).then(res=>{
			if(res.error) return Toast.error(res.message);
			setCreateStack([]);
			var seats = getSeatsClone();
			seats = seats.filter(a=>!a.deleted);
			for(var i of seats){
				i.dirty = false;
			}
			if(res.data && res.data.new_seats){
				for(var s of res.data.new_seats){
					var sx = seats.findIndex(a=>a.seat_x==s.seat_x && a.seat_y==s.seat_y && !a.deleted);
					if(sx==-1) continue;
					seats[sx].seat_id = s.seat_id;
				}
			}
			setSeats(seats);
			setSeatmap({
				...seatmap,
				dirty: false
			});
			Toast.success('Se ha guardado el mapa de butacas.');
		}).catch(err=>{
			Toast.error('Hubo un error inesperado guardando las butacas (LCL-1)');
		}).finally(()=>{
			setSending(false);
		})
	}

	const openCommentSeatModal = (status_id: number) => {
		setSeatStatusM(status_id)
		if (status_id !== SeatStatus.FREE) {
			setSeatComment('');
			setShownModal(ModalType.SEAT_COMMENT);
		} else {
			setSeatStatus(status_id);
		}
	}

	const setSeatZoom = (amt: number) => {
		return () => {
			setSeatSize(Math.max(Math.min(seatSize+amt, 1.5), 0.5));
		}
	}
	
	var onRowsConfigChange = bindFormChange(configRows, setConfigRows);
	var onColsConfigChange = bindFormChange(configCols, setConfigCols);
	var onSingleConfigChange = bindFormChange(singleConfig, setSingleConfig);
	var onDrawSettingsChange = bindFormChange(drawSettings, setDrawSettings);

	return <div>
		<Groupper fitted title={date_id ? <>
			{section?.section_name} <br />
			<div style={{ lineHeight: '12px', fontSize: 12, textTransform: 'none', color: 'brown' }}>Sección de calendario #{date_id}</div>
		</> : section?.section_name} className='map' titleRight={!!date_id && (
			<Label color='blue' style={{ textTransform: 'none' }}>Calendario #{date_id}</Label>
		)}>
			<div className="ar toolbar">
				<Popup
					className='seatmap-edit'
					trigger={(
						<div className="item">
							<Icon name='crop' /> Mapa
						</div>
					)}
				>
					<Field label='Columnas' labelStyle={{ textAlign: 'center' }}>
						<div className="fr increment">
							<Button className='minus' color='black' iconName="minus" onClick={()=>updateMapColumns(-1)} />
							<div className="amount">{seatmap.seats_width}</div>
							<Button className='plus' color='black' iconName="plus" onClick={()=>updateMapColumns(1)} />
						</div>
					</Field>
					<Field label='Filas' labelStyle={{ textAlign: 'center' }}>
						<div className="fr increment">
							<Button className='minus' color='black' iconName="minus" onClick={()=>updateMapRows(-1)} />
							<div className="amount">{seatmap.seats_height}</div>
							<Button className='plus' color='black' iconName="plus" onClick={() => updateMapRows(1)} />
						</div>
					</Field>
					<div style={{ display: 'flex', height: 40 }}>
						<Button className='zoom out' icon iconStyle={{ fontSize: 16 }} onClick={setSeatZoom(-0.1)} iconName='magnifying-glass-minus' /> 
						<Button className='zoom in' icon iconStyle={{ fontSize: 16 }} onClick={setSeatZoom(0.1)} iconName='magnifying-glass-plus' />
					</div>
				</Popup>
				<div className={classNames("item", { black: selectMode===SelectMode.DRAW })} onClick={selectMode===SelectMode.DRAW ? bindClick(setSelectMode, SelectMode.EVERYTHING) : startDrawMode}>
					<Icon name='pencil' /> Dibujar
				</div>
				<div className={classNames("item", { disabled: !has_selection })} onClick={showNumberModal}>
					<Icon name='hashtag' /> Numerar
				</div>
				<Menu 
					// disabled={!has_selection} 
					trigger={(
						<div className={`item ${!!has_selection ? '' : 'disabled'}`}>
							<Icon name='pencil' /> Editar
						</div>
					)}
					items={[
						{ 
							text: 'Estado', 
							items: SEAT_STATUS_LOCALE.map(s=>(
								{ text: s.name, onClick: ()=> openCommentSeatModal(s.id) }
							)),
						},
						{ text: 'Asignar sección', onClick: bindClick(setShownModal, ModalType.SECTION) },
						{ text: 'Escenario', onClick: setStage },
					]}
				/>
				<Popup
					className='seatmap-edit'
					// disabled={!has_selection}
					trigger={(
						<div className={classNames("item", {
							disabled: !has_selection
						})}>
							<Icon name='up-down-left-right' /> Mover
						</div>
					)}
				>
					<div className='fr centered'>
						<Button icon iconName='arrow-up' color='black' onClick={moveSeats(0, -1)} />
					</div>
					<div className='fr centered'>
						<Button icon iconName='arrow-left' color='black' onClick={moveSeats(-1, 0)} />
						<div className="fr blank"></div>
						<Button icon iconName='arrow-right' color='black' onClick={moveSeats(1, 0)} />
					</div>
					<div className='fr centered'>
						<Button icon iconName='arrow-down' color='black' onClick={moveSeats(0, 1)} />
					</div>
				</Popup>
				<Menu
					trigger={(
						<div className='item'>
							<Icon name='mouse-pointer' /> Selección
							{(selectMode!==SelectMode.EVERYTHING && selectMode!==SelectMode.DRAW) && (
								<Icon name='asterisk' color='brown' style={{ marginRight: 0, marginLeft: 5 }} />
							)}
						</div>
					)}
					items={[
						{ text: 'Deseleccionar todo', iconName: 'undo', onClick: deselect },
						{ text: 'Deshacer', iconName: 'undo', onClick: popSelection },
						{ text: 'Dibujar butacas', iconName: 'pen', onClick: bindClick(setShownModal, ModalType.DRAW_SEATS) },
						{ text: 'Solo butacas', iconName: selectMode===SelectMode.SEATS ? 'check-circle' : 'circle', iconSolid: selectMode===SelectMode.SEATS, onClick: selectModeChanged(selectMode===SelectMode.SEATS ? SelectMode.EVERYTHING : SelectMode.SEATS) }
					]}
				/>
				{/* <Dropdown icon={null} additionPosition='bottom' disabled={selectMode===SelectMode.DRAW} trigger={(
				)}>
					<Dropdown.Menu style={{ width: '100%' }}>
						<Dropdown.Item onClick={deselect}>
							<Icon name='undo' />
							Deseleccionar todo
							<div className="description" style={{ fontSize: 12 }}>(Ctrl + D)</div>
						</Dropdown.Item>
						<Dropdown.Item onClick={popSelection}>
							<Icon name='undo' />
							Deshacer
							<div className="description" style={{ fontSize: 12 }}>(Ctrl + Z)</div>
						</Dropdown.Item>
						<Dropdown.Item onClick={bindClick(setShownModal, ModalType.DRAW_SEATS)}>
							<Icon name='pen' />
							Dibujar butacas
						</Dropdown.Item>
						<Dropdown.Item onClick={selectModeChanged(selectMode===SelectMode.SEATS ? SelectMode.EVERYTHING : SelectMode.SEATS)}>
							<Icon name={selectMode===SelectMode.SEATS ? 'check-circle' : 'circle'} solid={selectMode===SelectMode.SEATS} />
							Solo butacas
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown> */}
				{/* <div className="item" onClick={deselect}>
					<Icon name='ban' /> Deseleccionar
				</div> */}
				{/* <div className={classNames("item")} onClick={() => {setShownModal(ModalType.SEAT_LABEL)}}>
					<Icon name='info' style={{ margin: 0 }} />
				</div> */}
				<div className={classNames('item', {
					black: stage_dirty,
					'fr loading': sending
				})} onClick={sendSave}>
					<Icon name="save" /> Guardar
				</div>
			</div>
			<div className='contents'>
				<div className="fr tooled seatmap" onMouseDown={onSelectStart} onMouseMove={onSelectMove} onMouseUp={onSelectEnd} onContextMenu={onContextMenu}>
					<div className="seats">
						{map.map((row, y)=>(
							<div className="row" key={`smrw-${y}`}>
								{row.map((seat, x)=>{
									var seatRef = x==0 && y==0 ? firstSeatRef : null;
									var key = 'smpst-'+(seat?.seat_id || `(${x}, ${y})`)
									var is_stage = isStage(x, y);
									var selected = isSelected(x, y);

									if(is_stage){
										return <Seatmap.Seat key={key} ref={seatRef} color='black' className={classNames({ selected })} stage={[1,1,1,1]} style={effective_seat_size.seat_style}>E</Seatmap.Seat>
									}
									if(!seat){
										var is_ghost = ghosts.find(a=>a.x===x && a.y===y);
										if(is_ghost){
											return <Seatmap.Seat empty className={classNames('ghost', { selected })} style={effective_seat_size.seat_style} key={key} ref={seatRef} />
										}
										if(selectMode===SelectMode.DRAW && hoverSeat && hoverSeat.x===x && hoverSeat.y===y){
											var ds = getDrawSeat(hoverSeat, drawSettings.last_draw);
											return <Seatmap.Seat 
												key={key} 
												ref={seatRef}
												className='ghost draw' 
												style={effective_seat_size.seat_style} 
												textStyle={effective_seat_size.text_style}
												subtext={drawSettings?.seat_section}
											>
												{formatSeatNumber(ds.seat_row, ds.seat_number)}
											</Seatmap.Seat>
										}
										return <Seatmap.Seat empty className={classNames({ selected })} style={effective_seat_size.seat_style} key={key} ref={seatRef} />
									}
									var denominator = formatSeatNumber(seat.seat_row, seat.seat_number)

									return <Seatmap.Seat 
										statusIcon
										key={key} 
										ref={seatRef}
										className={classNames({
											new: !seat.seat_id || seat.dirty,
											error: seat_duplicates.indexOf(denominator)!=-1,
											selected
										})}
										status={seat.seat_status} 
										style={effective_seat_size.seat_style}
										textStyle={effective_seat_size.text_style}
										subtext={effective_seat_size.seat_height<35 ? null : seat.seat_section}
									>
										{denominator}
									</Seatmap.Seat>
								})}
							</div>
						))}
					</div>
				</div>
				{selectMode===SelectMode.DRAW && (
					<div className="sidebar">
						<Input label='Fila' comment='Hotkey: W / S' value={drawSettings?.seat_row || ''} onChange={onDrawSettingsChange('seat_row')} />
						<FRDropdown label='Dirección' value={drawSettings.move_row} onValueChange={onDrawSettingsChange('move_row')} items={[
							{ text: 'No mover', iconName: 'ban', value: SeatDirection.NO_MOVE },
							{ text: 'Hacia abajo', iconName: 'arrow-down', value: SeatDirection.TOP_BOTTOM },
							{ text: 'Hacia arriba', iconName: 'arrow-up', value: SeatDirection.BOTTOM_TOP },
						]} />
						<hr color='#CCC'></hr>

						<Input label='Butaca' comment='Hotkey: A / D' value={drawSettings?.seat_number || ''} onChange={onDrawSettingsChange('seat_number')} />
						<FRDropdown label='Dirección' value={drawSettings.move_number} onValueChange={onDrawSettingsChange('move_number')} items={[
							{ text: 'No mover', iconName: 'ban', value: SeatDirection.NO_MOVE },
							{ text: 'Hacia derecha', iconName: 'arrow-right', value: SeatDirection.LEFT_RIGHT },
							{ text: 'Hacia izquierda', iconName: 'arrow-left', value: SeatDirection.RIGHT_LEFT },
						]} />
						<hr color='#CCC'></hr>
						<Input label='Sección' value={drawSettings?.seat_section || ''} onChange={onDrawSettingsChange('seat_section')} />
					</div>
				)}
			</div>
		</Groupper>

		<Modal open={shownModal===ModalType.NUMBER} onClose={bindClick(setShownModal)} size={has_selection && (selection_bounds.width*selection_bounds.height)==1 ? 'mini' : 'large'}>
			<Modal.Header>Numeración</Modal.Header>
			<Modal.Content>
				{has_selection && (selection_bounds.width*selection_bounds.height)>1 && shownModal===ModalType.NUMBER ? <>
					<Header text='Preview' size='small' style={{ marginTop: -5 }} />
					<div className="fr small seatmap" style={{ height: 'auto' }}>
						<div className="seats">
							{getPreview(real_selection).map((row, ir) => (
								<div className="row" key={`PVW-r${ir}`}>
									{row.map((col, ic)=>(
										col ? (
											<Seatmap.Seat style={getSeatSize(0.6).seat_style} key={`PVW-r${ir}c${ic}`} text={formatSeatNumber(col.row, col.number)} />
										) : (
											<Seatmap.Seat style={getSeatSize(0.6).seat_style} empty key={`PVW-r${ir}c${ic}`} />
										)
									))}
								</div>
							))}
						</div>
					</div>
					<div style={{ maxWidth: 500, margin: 'auto', marginTop: 15 }}>
						<table className="fr details divided table" style={{ borderBottomRightRadius: 0, borderBottomLeftRadius: 0 }}>
							<thead>
								<tr>
									<th className='title' colSpan={2}>
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
											Fila
											<Checkbox checked={!!configRows} onChange={v=>{
												setConfigRows(v ? {
													direction: SeatDirection.TOP_BOTTOM,
													skip_empty: true,
												} : null);
											}} />
										</div>
									</th>
								</tr>
							</thead>
							{!!configRows ? (
								<tbody>
									<tr>
										<td>Valor inicial</td>
										<td>
											<Input 
												style={{ marginBottom: 0 }} 
												placeholder='Valor inicial' 
												onChange={determineConfigType(configRows, setConfigRows)} 
												error={configRows?.initial_value && configRows?.initial_value.length>0 && configRows?.type===null} 
											/>
										</td>
									</tr>
									<tr>
										<td>Dirección</td>
										<td>
											<Dropdown
												value={configRows?.direction}
												onValueChange={onRowsConfigChange('direction')}
												placeholder={'Direccion'}
												items={[{
													text: 'Arriba abajo',
													value: SeatDirection.TOP_BOTTOM,
													iconName: 'arrow-down'
												}, {
													text: 'Abajo arriba',
													value: SeatDirection.BOTTOM_TOP,
													iconName: 'arrow-up'
												}]}
											/>
										</td>
									</tr>
									<tr>
										<td>Contar vacias</td>
										<td>
											<Checkbox label={'Numerar filas vacias'} style={{ margin: 0 }} checked={configRows?.skip_empty} onChange={onRowsConfigChange('skip_empty')} />
										</td>
									</tr>
								</tbody>
							) : null}
						</table>
						<table className="fr details divided table" style={{ marginTop: 0, borderTopRightRadius: 0, borderTopLeftRadius: 0 }}>
							<thead>
								<tr>
									<th className='title' colSpan={2}>
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
											Butacas
											<Checkbox checked={!!configCols} onChange={v=>{
												setConfigCols(v ? {
													direction: SeatDirection.LEFT_RIGHT,
													skip_empty: true,
												} : null);
											}} />
										</div>
									</th>
								</tr>
							</thead>
							{!!configCols ? (
								<tbody>
									<tr>
										<td>Valor inicial</td>
										<td>
											<Input 
												style={{ marginBottom: 0 }} 
												placeholder='Valor inicial' 
												onChange={determineConfigType(configCols, setConfigCols)} 
												error={configCols?.initial_value && configCols?.initial_value.length>0 && configCols?.type===null} 
											/>
										</td>
									</tr>
									<tr>
										<td>Dirección</td>
										<td>
											<Dropdown
												value={configCols?.direction}
												onValueChange={onColsConfigChange('direction')}
												placeholder={'Direccion'}
												items={[{
													iconName: 'arrow-right',
													text: 'Izquierda derecha',
													value: SeatDirection.LEFT_RIGHT,
												}, {
													iconName: 'arrow-left',
													text: 'Derecha izquierda',
													value: SeatDirection.RIGHT_LEFT,
												}]}
											/>
										</td>
									</tr>
									<tr>
										<td>Contar vacias</td>
										<td>
											<Checkbox label={'Numerar columnas vacias'} style={{ margin: 0 }} checked={configCols?.skip_empty} onChange={onColsConfigChange('skip_empty')} />
										</td>
									</tr>
								</tbody>
							) : null}
						</table>
					</div>
				</> : has_selection && (selection_bounds.width*selection_bounds.height)==1 ? <>
					<Input label='Fila' value={singleConfig?.row} onChange={onSingleConfigChange('row')} />
					<Input label='Butaca' value={singleConfig?.number} onChange={onSingleConfigChange('number')} />
				</> : null}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				<Button text='Numerar' color='black' onClick={has_selection && (selection_bounds.width*selection_bounds.height)>1 ? numberSeats : numberSingleSeat} />
			</Modal.Actions>
		</Modal>
		{contextMenu ? (
			<Menu inline
				style={{ 
					position: 'absolute', 
					zIndex: 100, 
					left: contextMenu?.x || -1000, 
					top: contextMenu?.y || -1000, 
					width: 200, 
					borderRadius: 12,
					display: contextMenu ? 'block' : 'none',
				}}
				items={[
					{ text: 'Numerar', iconName: 'hashtag', onClick: showNumberModal },
					{ text: 'Crear butacas', disabled: seats_selected.length!=0, iconName: 'circle-plus', onClick: bindClick(setShownModal, ModalType.SECTION) },
					{ text: 'Asignar escenario', iconName: 'microphone', onClick: ()=>setStage(), if: !stage_selected },
					{ text: 'Eliminar escenario', iconName: 'remove', onClick: removeStage, style: { color: 'brown' }, if: stage_selected },
					{ text: 'Estado', iconName: 'burger', disabled: seats_selected.length===0, items: SEAT_STATUS_LOCALE.map(s=>(
						{ text: s.name, onClick: ()=> openCommentSeatModal(s.id) }
					)) },
					{ text: 'Agregar', iconName: 'circle-plus', items: [
						{ text: 'Fila arriba', iconName: 'arrow-up', onClick: ()=>addRow(selection_bounds.start_y), disabled: !has_selection },
						{ text: 'Columna izquierda', iconName: 'arrow-left', onClick: ()=>addColumn(selection_bounds.start_x), disabled: !has_selection },
					] },
					{ text: 'Eliminar', iconName: 'circle-xmark', items: [
						{ text: 'Fila', iconName: 'grip-horizontal', onClick: ()=>removeRow(selection_bounds.start_y, selection_bounds.end_y), disabled: real_selection.length!=1 || seats_selected.length>0 },
						{ text: 'Columna', iconName: 'grip-vertical', onClick: ()=>removeColumn(selection_bounds.start_x), disabled: real_selection.length!=1 || seats_selected.length>0 },
						{ text: `Eliminar BUTACA${seats_selected.length==1 ? '' : 'S'}`, iconName: 'chair', onClick: showDeleteModal, disabled: !seats_selected || seats_selected.length==0, style: { color: 'brown' }},
					] }
				]}
			/>
		) : null}
		<Modal open={shownModal===ModalType.SECTION} onClose={bindClick(setShownModal)} size='mini'>
			<Modal.Header>Asignar Seccion</Modal.Header>
			<Modal.Content>
				<Input
					label='Sección de las butacas'
					comment='La sección del grupo de butacas (Ej. Mesa 1, Palco 32)'
					value={sectionName}
					onChange={setSectionName}
				/>
				{seats_selected.length==0 && (
					<Message type='info'>
						No se han seleccionado butacas ya creadas, se crearán las butacas en los espacios seleccionados con solo la sección ingresada
					</Message>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				<Button text='Guardar' color='black' onClick={setSeatSection} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===ModalType.DELETE_SEATS} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Eliminar Butacas</Modal.Header>
			<Modal.Content>
				<Header size='small' text='¿Eliminar las butacas seleccionadas?' />
				<ul style={{ paddingLeft: 15 }}>
					<li>Una vez eliminadas, despues de guardar el mapa de butacas no podrán ser recuperadas</li>
					<li>Si hay boletos vendidos con estas butacas en algún calendario <b>NO</b> serán cancelados</li>
					<li>Se podrán crear nuevas butacas en el mismo lugar pero <b>NO</b> serán las mismas butacas, ya que el ID de la butaca cambia.</li>
				</ul>
				{seats_selected.length<=0 && (
					<Header size='small' text='No hay butacas seleccionadas.' />
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				{seats_selected.length>0 ? (
					<Button text='Eliminar' color={'red'} onClick={deleteSeats} />
				) : null}
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===ModalType.SEAT_COMMENT} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Cambio de Estatus</Modal.Header>
			<Modal.Content>
				<Header size='small' text='¿Porque estas cambiando de estado las butacas?' />
				<Input 
					value={seatComment} 
					onChange={setSeatComment} 
					label='Comentario' 
					comment={'Razón del cambio de estado de las butacas'}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Guardar' color={'black'} onClick={() => { setSeatStatus() }} />
			</Modal.Actions>
		</Modal>
		<SeatmapDrawModal open={shownModal===ModalType.DRAW_SEATS} onClose={bindClick(setShownModal)} map={map} ghosts={ghosts} onChange={setGhosts} />
		<Modal open={shownModal === ModalType.SEAT_LABEL} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Leyenda</Modal.Header>
			<Modal.Content>
				<Table fitted striped divided >
					{SEAT_STATUS_LOCALE.map(s=>(
						<Table.Row collapsingIndexes={[0]} key={s.id}
							data={[
								<Seatmap.Seat status={s.id} iconName={classNames({
									ban: s.id === SeatStatus.BLOCKED,
									lock: s.id === SeatStatus.LOCKED,
									'eye-slash': s.id === SeatStatus.HIDDEN,
								}) as any} />,
								s.name,
							]}
						/>
					))}
					<Table.Row
						data={[
							<Seatmap.Seat stage={[1,1,1,1]} text='E' />,
							'Escenario'
						]}
					/>
				</Table>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default SeatmapEdit;